import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import type { ComponentProps, ElementType } from "react";

export function SidebarRoot({
  children,
  className,
  isHidden = false,
  side = "right",
  ...rest
}: { isHidden?: boolean; side?: "left" | "right" | "right-bottom" } & ComponentProps<"aside">) {
  const isFullHeight = side === "left" || side === "right";
  return (
    <aside
      className={twMerge(
        "z-50 flex flex-col pointer-events-none *:pointer-events-auto",
        "sm:hidden",
        isFullHeight && "sticky h-screen top-0",
        side === "right" && "right-0",
        side === "right-bottom" && "fixed right-0 bottom-0",
        isHidden ? "w-0 min-w-0 p-0" : "w-sidebar-width min-w-sidebar-width p-2.5",
        className,
      )}
      {...rest}
    >
      {children}
    </aside>
  );
}

export function SidebarScrollable({
  as: Component = "div",
  children,
  className,
  ...rest
}: { as?: "div" | "nav" } & ComponentProps<"div">) {
  return (
    <Component
      className={twMerge("relative overflow-y-auto no-scrollbar w-full", className)}
      {...rest}
    >
      {children}
    </Component>
  );
}

function SidebarGroup({
  children,
  shrink,
  className,
}: { shrink?: boolean } & ComponentProps<"div">) {
  return (
    <div
      className={twMerge(
        "flex rounded-12 w-full p-1.25",
        "[&:not(:last-child)]:mb-2.5",
        shrink && "flex-shrink overflow-hidden",

        "bg-block-bg shadow-block dark:shadow-block-inverted",

        className,
      )}
    >
      {children}
    </div>
  );
}

function SidebarGroupSection({ children, className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={twMerge(
        "rounded-8 mt-1.25 p-1.25 bg-gray-100 dark:bg-gray-900 relative",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function SidebarDelimiter({ className }: ComponentProps<"hr">) {
  return <hr className={twMerge("my-3 mx-1.5 border-gray-300 dark:border-gray-600", className)} />;
}

function SidebarSectionTitle({ children, className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={twMerge(
        "font-mono text-xxs tracking-tight uppercase flex items-center p-1.25 w-full",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

type SidebarSectionButtonProps<T extends ElementType> = {
  open?: boolean;
  as?: T;
  titleClassName?: string;
  wrapperClassName?: string;
} & Omit<ComponentProps<T>, "as">;

function SidebarSectionButton<T extends ElementType = "button">({
  children,
  open,
  className,
  as,
  titleClassName,
  wrapperClassName,
  ...props
}: SidebarSectionButtonProps<T>) {
  const Component = as || "button";

  return (
    <Component className={className} {...props}>
      <SidebarSectionTitle className={twMerge("flex px-0", wrapperClassName)}>
        <Icon name="ChevronDown" className={twMerge("flex-shrink-0", !open && "-rotate-90")} />
        <span className={twMerge("tracking-tight w-full text-left", titleClassName)}>
          {children}
        </span>
      </SidebarSectionTitle>
    </Component>
  );
}

export {
  SidebarGroup as Group,
  SidebarGroupSection as GroupSection,
  SidebarRoot as Root,
  SidebarScrollable as Scrollable,
  SidebarDelimiter as Delimiter,
  SidebarSectionTitle as SectionTitle,
  SidebarSectionButton as SectionButton,
};
